import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useRoomState } from "@/lib/gameUi";
import { SettingsPanel, JoinSelf, GameController, HostConsole } from "@/components/game/mobile";

export default function HostControl() {
  const [params] = useSearchParams();
  const code = (params.get("code") || "").toUpperCase();
  const htUrl = params.get("ht");

  const stored = (() => { try { return JSON.parse(localStorage.getItem(`tp_control_${code}`) || "null"); } catch { return null; } })();
  const [hostToken] = useState(htUrl || stored?.host_token || null);
  const [session, setSession] = useState(stored?.session || null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (code && hostToken) localStorage.setItem(`tp_control_${code}`, JSON.stringify({ host_token: hostToken, session }));
  }, [code, hostToken, session]);

  const { state, error } = useRoomState(code, session?.player_id);

  if (!code || !hostToken) {
    return <div className="neon-bg min-h-screen flex flex-col items-center justify-center px-8 text-center"><p className="font-display font-bold text-2xl text-white">Invalid host link</p><p className="text-white/60 font-head mt-2">Scan the Host QR from the TV screen.</p></div>;
  }
  if (!state) {
    return <div className="neon-bg min-h-screen flex flex-col items-center justify-center gap-4"><Loader2 className="w-10 h-10 text-[#FF2A85] animate-spin" /><p className="text-white/60 font-head">{error ? "Reconnecting…" : "Connecting…"}</p></div>;
  }

  const openLobby = async (settings) => {
    setBusy(true);
    try {
      await api.post(`/rooms/${code}/settings`, { host_token: hostToken, settings });
      await api.post(`/rooms/${code}/open-lobby`, { host_token: hostToken });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open lobby"); }
    setBusy(false);
  };

  const leave = () => { localStorage.removeItem(`tp_control_${code}`); setSession(null); };

  // Phase 1: configure game (TV shows Host QR)
  if (state.status === "setup") {
    return (
      <div className="neon-bg min-h-screen flex flex-col relative">
        <div className="relative z-10 flex flex-col min-h-screen">
          <div className="text-center mt-6">
            <h1 className="font-display font-black text-3xl uppercase tracking-tight"><span className="text-white text-glow-magenta">Gimme a</span><span className="text-[#00F0FF]">sQuizz</span></h1>
            <p className="text-[#FFD700] font-head text-sm mt-1">Host · Game {code}</p>
          </div>
          <SettingsPanel onStart={openLobby} busy={busy} code={code} hostToken={hostToken} />
        </div>
      </div>
    );
  }

  // Phase 2: host joins as a player — only possible while the lobby is open.
  // If the host returns mid-game without a saved seat, give them the Host Console to drive the game.
  if (!session) {
    if (state.status !== "lobby") {
      return <HostConsole code={code} hostToken={hostToken} />;
    }
    return (
      <div className="neon-bg min-h-screen flex flex-col relative">
        <div className="relative z-10 flex flex-col min-h-screen">
          <div className="text-center mt-6 mb-2">
            <h1 className="font-display font-black text-3xl uppercase tracking-tight"><span className="text-white text-glow-magenta">Gimme a</span><span className="text-[#00F0FF]">sQuizz</span></h1>
            <p className="text-white/50 font-head text-sm mt-1">Game {code}</p>
          </div>
          <JoinSelf code={code} onJoined={setSession} isHost />
        </div>
      </div>
    );
  }

  // Phase 3+: play with host controls
  return <GameController code={code} session={session} isHost hostToken={hostToken} onLeave={leave} roomState={{ state, error }} />;
}
